import { Component, createRef } from 'react'
import { FormSection, Field } from 'redux-form'
import clx from 'classnames'
import _ from 'lodash'
import { Typography } from 'antd'
import { Icon, Button, Tip } from '@vzmi/shp_shared_compo'
import { numeralFormat } from '../../../../../../lib/renderUtil'
import { MenuSelectField, InputCountField, CheckboxInputField, HyperLink } from '../../../../Common/Form'
import TooltipIcon from '../../../../Common/TooltipIcon/TooltipIcon'
import SortableTable, {
  DragHandler
} from '../../../../Common/SortableTable/SortableTable'
import NoData from '../../../../Common/NoData/NoData'
import { getDuplicatedSkuInSpecsList } from '../../../../BatchProposal/Common/PrefixBlock/Duplicated'
import styles from './index.css'
import listingModelStyles from '../index.css'
import {
  checkIsFakeProductId,
  getAttrMap,
  getIsAllowNewProduct,
  getTableField,
  PRODUCT_ID_FAKE_PREFIX
} from '../helpers'
import { PRODUCT_KEY, PRODUCT_MODAL_TYPE } from '../constants'
import VisibleButton from '../VisibleButton'
import { NewProductBtnWithPopup } from '../NewProductBtn'
const { Paragraph } = Typography

export const FORM_SECTION_NAME = 'updateListingModelTable'

export const COLUMN = {
  DRAG: 'drag',
  NAME: 'name',
  SPEC_SELECTED_VALUE: 'specSelectedValue',
  ITEM_SPEC_SELECTED_VALUE: 'itemSpecSelectedValue',
  STOCK: 'stock',
  PART_NO: 'partNo',
  ATTRIBUTES: 'attributes'
}

const SELECTED_VALUE_TOOLTIP_MSG = '項目設定字數與商品名稱字數總和需在 100 字以內'

const ATTRIBUTES_TOOLTIP_MSG = '選擇符合之屬性項目，增加搜尋精準度'

const ATTRIBUTES_SYNC_TIP_MSG = '調整「項目顯示名稱」將連動商品名稱修改'

const DEFAULT_SPEC_MAX_SIZE = 100

const DEFAULT_ATTRIBUTE_OPTIONS = [{ label: '其他', value: '其他' }]

export const getRowClassName = (record) => {
  const classNames = [styles.modelTableRow]
  if (record.isUpdatedCollection) {
    classNames.push(styles.isUpdatedCollection)
  }
  if (record.isNew) {
    classNames.push('isNew')
  }
  return clx(...classNames)
}

export const getSpecOptions = (rows) => {
  const uniqSpecOptions = _.flow(
    rows => _.reduce(rows, (options, row) => {
      const specSelectedValue = _.get(row, PRODUCT_KEY.SPEC_SELECTED_VALUE)
      if (!_.isUndefined(specSelectedValue)) {
        options.push({ label: specSelectedValue, value: specSelectedValue })
      }
      return options
    }, []),
    specOptions => _.uniqBy(specOptions, 'value')
  )(rows)
  return uniqSpecOptions
}

// redux field `[FORM_NAME].[PRODUCT_ID]_[FIELD_NAME]`
export const getProductIdFromFormField = (field) => {
  const productId = _.get(
    field.match(`${FORM_SECTION_NAME}.(\\d*)_`),
    '1'
  )
  return `${productId}`
}

export const getFakeProductIdFromFormField = (field) => {
  const productId = _.get(
    field.match(`${FORM_SECTION_NAME}.(${PRODUCT_ID_FAKE_PREFIX}\\d*)_`),
    '1'
  )
  return `${productId}`
}

export const customSelectSpecValidation = (value, limit) => {
  if (_.size(`${value}`) > limit) {
    return `最多輸入${limit}個字`
  }

  return null
}

export const getSpecCountLimit = (listingName, rows, column) => {
  const attrLevel = _.get(rows, '0.attrLevel')
  const isTwoLayerAttr = (attrLevel === 2)
  const listingNameSize = _.size(listingName)
  let countLimit = DEFAULT_SPEC_MAX_SIZE - attrLevel - listingNameSize
  if (isTwoLayerAttr) {
    let specKey
    if (column === COLUMN.ITEM_SPEC_SELECTED_VALUE) {
      specKey = PRODUCT_KEY.SPEC_SELECTED_VALUE
    } else if (column === COLUMN.SPEC_SELECTED_VALUE) {
      specKey = PRODUCT_KEY.ITEM_SPEC_SELECTED_VALUE
    }
    const specMaxSize = _.max(
      _.map(rows, row => {
        const specSelectedValue = _.get(row, specKey)
        const currentValue = _.isArray(specSelectedValue)
          // 來自新增的商品屬性可能是從選單選出來的就會是陣列，因此計算時需要取第一個值
          ? _.first(specSelectedValue)
          : specSelectedValue
        return _.size(currentValue)
      })
    )
    countLimit -= specMaxSize
  }
  // 限制不可小於 0
  return _.max([countLimit, 0])
}

// 輸入驗證是整個表格內輸入都會執行一次，因為全部單雙層屬性都需要互相驗證是否為唯一值
export const customInputSpecValidation = (listingName, rows) => {
  const isTwoLayerAttr = (_.get(rows, '0.attrLevel') === 2)
  const attrMap = getAttrMap(isTwoLayerAttr)
  const rowsGroupBySku = _.keyBy(rows, attrMap.productId)
  const rowsGroupBySpec = _.groupBy(rows, PRODUCT_KEY.SPEC_SELECTED_VALUE)
  let rowsGroupByItemSpec = {}
  if (isTwoLayerAttr) {
    rowsGroupByItemSpec = _.groupBy(rows, PRODUCT_KEY.ITEM_SPEC_SELECTED_VALUE)
  }
  return (spec, allValues, props, field = '') => {
    // layer1 field `updateListingModelTable.6827114_specSelectedValue_0`
    // layer2 field `updateListingModelTable.6824010_itemSpecSelectedValue_1`
    // layer2 field `updateListingModelTable.fake_6824010_itemSpecSelectedValue_1`
    const map = field.includes(COLUMN.SPEC_SELECTED_VALUE)
      ? rowsGroupBySpec
      : rowsGroupByItemSpec
    const fakeId = getFakeProductIdFromFormField(field)
    const isFakeId = checkIsFakeProductId(fakeId)
    const productId = isFakeId
      ? fakeId
      : getProductIdFromFormField(field)
    const targetRow = _.get(rowsGroupBySku, productId, {})
    if (!_.isEmpty(targetRow) && _.isUndefined(spec)) {
      return '不可為空值'
    }

    const column = isTwoLayerAttr
      ? COLUMN.ITEM_SPEC_SELECTED_VALUE
      : COLUMN.SPEC_SELECTED_VALUE
    const countLimit = _.max([getSpecCountLimit(listingName, rows, column), 0])
    if (_.size(`${spec}`) > countLimit) {
      return `最多輸入 ${countLimit} 個字`
    }

    const sameSpecs = _.get(map, spec)
    const currentParentSpec = _.get(targetRow, PRODUCT_KEY.SPEC_SELECTED_VALUE)
    const sameParentSpecRows = _.get(rowsGroupBySpec, currentParentSpec)
    const isSameSpecsExist = _.size(sameSpecs) > 1
    const isSameParentSpecExist = isTwoLayerAttr
      ? _.size(
        _.filter(sameParentSpecRows, row => {
          const {
            [PRODUCT_KEY.SPEC_SELECTED_VALUE]: specSelectedValue,
            [PRODUCT_KEY.ITEM_SPEC_SELECTED_VALUE]: itemSpecSelectedValue
          } = row
          return (
            specSelectedValue === currentParentSpec &&
            itemSpecSelectedValue === spec
          )
        })
      ) > 1
      : true
    if (isSameParentSpecExist && isSameSpecsExist) {
      return '不可輸入已存在的項目'
    }

    return null
  }
}

export const customAttributesValidation = () => values => {
  if (_.isEmpty(values)) {
    return '不可為空值'
  }

  return null
}

export const customProductNameValidation = isExpress24Hr => (productName) => {
  if (_.isEmpty(productName)) {
    return '不可為空值'
  }

  if (isExpress24Hr && (_.size(productName) > 50)) {
    return (
      <>
        <br />
        進倉用標籤/品名需為50個字內，請調整並確認內容是可供倉庫辨識的
      </>
    )
  }

  if (!isExpress24Hr && (_.size(productName) > 100)) {
    return '字數超過限制'
  }

  return null
}

export const getSelectedProductIds = tableValues => {
  const selectedProductIds = _.flow(
    // 表格表單的欄位在 redux form 全部都會被存成物件的一個屬性
    () => _.keys(tableValues),
    // 全部取出後找到屬於批次移除 checkbox 的欄位
    formFields => _.map(formFields, field => {
      const productId = getProductIdFromSelectedField(field)
      // checkbox 值為 false 代表沒有被選為批次移除項目
      if (_.isEmpty(productId) || !tableValues[field]) {
        return null
      }

      return productId
    }),
    productIds => _.filter(productIds, id => !_.isEmpty(id))
  )()
  return selectedProductIds
}

export const getActionsVisibleMapBySelectedValue = (props) => {
  const {
    isTwoLayerAttr,
    dataMap,
    withActions,
    sortList,
    form,
    tableRows,
    batchRemoveMode
  } = props
  const tableForm = _.get(form, FORM_SECTION_NAME, {})
  const attrMap = getAttrMap(isTwoLayerAttr)
  let currentTableRows = tableRows
  let currentDataMap = dataMap
  // 批次移除模式下可操作的商品屬性列需要過濾掉被勾選的商品屬性
  // 因為被勾選的要當作已經被移除，才能讓剩下未被勾選的商品屬性判斷是否能繼續被勾選為批次移除
  if (batchRemoveMode) {
    // ex _.keyBy([1, 2, 33])
    // return { 1: 1, 2: 2, 33: 33 }
    const removedProductIdMap = _.keyBy(getSelectedProductIds(tableForm))
    currentTableRows = _.filter(tableRows, (row) => {
      const rowId = `${_.get(row, attrMap.productId)}_${row.index}`
      return !(rowId in removedProductIdMap)
    })
    // 雙層屬性賣場會按照第一層屬性判斷是否能被移除
    // 因此需要從扣掉被勾選為批次移除重新組出按照第一層分群的表格列
    currentDataMap = _.groupBy(currentTableRows, PRODUCT_KEY.SPEC_SELECTED_VALUE)
  }
  // 不需要操作功能或是整個賣場屬性只有一筆時不顯示功能按鈕
  if (!withActions || (_.size(currentTableRows) === 1)) {
    return {}
  }

  const totalVisibleRowSize = _.flow(
    rows => _.filter(rows, row => {
      return (
        _.get(row, PRODUCT_KEY.SPEC_VISIBLE) &&
        _.get(row, PRODUCT_KEY.ITEM_SPEC_VISIBLE)
      )
    }),
    _.size
  )(currentTableRows)
  const actionsVisibleMap = _.reduce(sortList, (map, selectedValue) => {
    const currentRows = isTwoLayerAttr
      // 雙層屬性賣場根據目前表格列的第一層屬性找出同一屬性的第二層屬性
      ? _.get(currentDataMap, selectedValue, [])
      : currentTableRows
    const currentSpecVisibleRowSize = _.flow(
      // 找出狀態為顯示(雙層看第二層屬性的狀態)的屬性表格列
      rows => _.filter(rows, row => _.get(row, attrMap.specVisible)),
      _.size
    )(currentRows)
    const isVisibleRowsMoreThenOne = currentSpecVisibleRowSize > 1
    map[selectedValue] = {
      // 顯示狀態按鈕需要有一筆以上還是顯示狀態才可以調整
      isVisibleBtn: isVisibleRowsMoreThenOne,
      // 刪除屬性按鈕需要有一筆以上還是顯示狀態或是
      // 雙層時全部顯示狀態比數大於一，目前第一層屬性中第二層只剩下一筆顯示且為顯示狀態
      isDeleteBtn: (
        isVisibleRowsMoreThenOne ||
        (
          isTwoLayerAttr &&
          (
            totalVisibleRowSize > 1 &&
            currentSpecVisibleRowSize === 1 &&
            currentSpecVisibleRowSize === _.size(currentRows)
          )
        )
      )
    }
    return map
  }, {})
  return actionsVisibleMap
}

/**
 * 單層屬性賣場調整頁時直接在商品屬性的表頭後面加上說明 icon
 * 雙層的情況下第一層屬性後面會有修改第一層排序的 icon 所以說明的 icon 放到第二層屬性的表頭後面
 */
const getFisrtLayerIcon = (props) => {
  const {
    isTwoLayerAttr,
    columnsProps,
    readOnly,
    sortList,
    batchRemoveMode
  } = props
  const editable = !readOnly
  const isMultipleSpec = (_.size(sortList) > 1)
  let icon = null
  if (isTwoLayerAttr) {
    if (isMultipleSpec && !batchRemoveMode) {
      icon = (
        <Icon
          name='compose'
          color='#000000d9'
          align='middle'
          size={14}
          className={styles.updateProductSpec}
          onClick={columnsProps.onSpecSortModalOpen}
        />
      )
    }
  } else if (editable) {
    icon = (
      <TooltipIcon tooltipTitle={SELECTED_VALUE_TOOLTIP_MSG} />
    )
  }
  return icon
}

const getRowId = (productId, record) => `${COLUMN.DRAG}${productId}_${record.index}`

const REMOVE_MODE_ROW_SUFFIX = '_selected'
const getRemoveModeRowId = rowId => `${rowId}${REMOVE_MODE_ROW_SUFFIX}`

export const getProductIdFromSelectedField = selectedField => _.get(
  `${selectedField}`.match(new RegExp(`^${COLUMN.DRAG}(\\d+_\\d+)${REMOVE_MODE_ROW_SUFFIX}`)),
  '1'
)

export const checkIsNewProduct = record => {
  const isNewProduct = _.get(record, 'isNewProduct', false)
  return isNewProduct
}

const getColumns = (props, tableProps, rows) => {
  const {
    isTwoLayerAttr,
    isExpress24Hr,
    columnsProps,
    draggable,
    readOnly,
    batchRemoveMode,
    form,
    form: {
      listing: {
        listingSpecName: specName,
        listingItemSpecName: itemSpecName
      } = {}
    } = {},
    attributesOptionsMapByName = {},
    attrsFeatureToggle
  } = props
  const {
    debounceAttrChange,
    customInputSpecValidation,
    customProductNameValidation,
    customAttributesValidation,
    getCountLimit,
    getActionsVisibleMap
  } = tableProps
  const tableForm = _.get(form, FORM_SECTION_NAME, {})
  const models = _.get(form, 'models', [])
  const attrMap = getAttrMap(isTwoLayerAttr)
  const { specCountLimit, itemSpecCountLimit } = getCountLimit()
  const specOptions = isTwoLayerAttr ? getSpecOptions(rows) : []
  const editable = !readOnly && !batchRemoveMode
  const textOnly = !draggable
  const duplicatedSkuMap = _.keyBy(getDuplicatedSkuInSpecsList(models), 'sku')
  return [
    {
      key: 'sortableIcon',
      width: 50,
      align: 'center',
      render: (text, record) => {
        const productId = _.get(record, attrMap.productId)
        const rowId = getRowId(productId, record)
        if (!draggable) {
          return <span id={rowId} />
        }

        if (batchRemoveMode) {
          const removeModeRowId = getRemoveModeRowId(rowId)
          const actionsVisibleMap = getActionsVisibleMap()
          const specSelectedValue = _.get(record, PRODUCT_KEY.SPEC_SELECTED_VALUE)
          const visibleKey = isTwoLayerAttr ? specSelectedValue : `${productId}_${specSelectedValue}`
          const { isDeleteBtn } = _.get(actionsVisibleMap, visibleKey, {})
          const isVisible = _.get(record, attrMap.specVisible)
          const isRowSelected = _.get(tableForm, removeModeRowId)
          return (
            <Field
              name={removeModeRowId}
              id={rowId}
              component={CheckboxInputField}
              // 會在 getActionsVisibleMapBySelectedValue 一次算好批次勾選的狀態
              // isVisible 商品為非隱藏狀態才有移除限制，隱藏狀態時可以任意移除
              disabled={!isDeleteBtn && !isRowSelected && isVisible}
            />
          )
        }

        return (
          <DragHandler
            id={rowId}
            disabled={readOnly}
          />
        )
      }
    },
    {
      title: '商品編號 / 商品名稱',
      width: 200,
      dataIndex: COLUMN.NAME,
      key: COLUMN.NAME,
      render: (text, record) => {
        const productId = _.get(record, attrMap.productId)
        const isFakeProductId = checkIsFakeProductId(productId)
        const productName = _.get(record, attrMap.productName)
        const { [PRODUCT_KEY.IS_NEW]: isNew, isUpdate: isUpdatedRow } = record
        const isUpdate = (
          // 非移除的商品屬性列
          !textOnly &&
          // 修改確認頁返回時，已修改的表格列會有被更新的狀態
          isUpdatedRow &&
          // 額外加上 proposal 是 readonly 才顯示前綴
          readOnly
        )
        const isUpdateName = _.get(record, `updatedField.${attrMap.productName}`)
        // 判斷是否為新增商品
        const isNewProduct = checkIsNewProduct(record) || isFakeProductId || _.isUndefined(productId)
        const column = COLUMN.NAME
        const productNameFieldName = getTableField(productId, column, record)
        // 編輯頁且非新增商品時顯示 input
        const isInputField = (!readOnly && !isNewProduct && attrsFeatureToggle)
        // 顯示假的 input(顯示完整換行內容 input 樣式)
        const isMockReadonlyInputField = (
          // 確認、檢視頁
          readOnly &&
          // 商品名稱有變更過或是新增商品
          (isUpdateName || isNew) &&
          attrsFeatureToggle
        )
        // 非以上兩種顯示純文字
        const isPureTextField = (!isInputField && !isMockReadonlyInputField)
        return (
          <>
            {
              (isNew || isUpdate) && (
                <span
                  className={clx(
                    styles.newRowPrefix,
                    { [styles.isUpdate]: isUpdate }
                  )}
                >
                  {
                    isNew
                      ? (
                        <>
                          <Icon
                            name='ec-flash'
                            color='#ec457d'
                            align='bottom'
                            size={24}
                          />
                          NEW
                        </>
                      )
                      : (
                        <>
                          <Icon
                            name='compose'
                            color='#ec457d'
                            align='top'
                            size={12}
                          />
                          UPDATED
                        </>
                      )
                  }
                </span>
              )}
            <span
              className={clx(
                { [styles.isDuplicated]: (productId in duplicatedSkuMap) }
              )}
            >
              {isFakeProductId ? '' : `${productId} `}
              {
                isNewProduct && (
                  <HyperLink
                    data-role='newProductInfo'
                    onClick={() => columnsProps.onProductModalOpen([record], PRODUCT_MODAL_TYPE.NEW)}
                  >
                    {'商品資訊 '}
                    {!readOnly && (
                      <Icon
                        name='compose'
                        color='#5478fc'
                        align='initial'
                        size={14}
                      />
                    )}
                  </HyperLink>
                )
              }
            </span>
            <br />
            {isPureTextField && (
              <Paragraph ellipsis={{ rows: 2, expandable: true, symbol: '看更多' }}>
                {productName}
              </Paragraph>
            )}
            {isMockReadonlyInputField && (
              <div className={styles.updatedProductName}>
                {productName}
              </div>
            )}
            {isInputField && (
              <Field
                name={productNameFieldName}
                component={InputCountField}
                // className={styles.specSelectedValue}
                onChange={e => debounceAttrChange(e, record, column)}
                countLimit={isExpress24Hr ? 50 : 100}
                validate={customProductNameValidation}
                disabled={!editable || isFakeProductId}
                placeholder='請輸入商品名稱'
                readOnly={readOnly}
                keepErrorPosition
              />
            )}
          </>
        )
      }
    },
    {
      title: (
        <span>
          {`${(specName || '無屬性')} `}
          {getFisrtLayerIcon(props)}
          {
            attrsFeatureToggle && (
              <>
                <br />
                屬性項目／項目顯示名稱
                {!isTwoLayerAttr && (
                  <TooltipIcon tooltipTitle={ATTRIBUTES_TOOLTIP_MSG} />
                )}
                <br />
                <Tip
                  type='info'
                  className={styles.attributesTip}
                >
                  {ATTRIBUTES_SYNC_TIP_MSG}
                </Tip>
                <br />
              </>
            )
          }
        </span>
      ),
      width: 200,
      dataIndex: COLUMN.SPEC_SELECTED_VALUE,
      key: COLUMN.SPEC_SELECTED_VALUE,
      render: (text, record) => {
        const {
          [PRODUCT_KEY.IS_NEW]: isNew,
          [attrMap.productId]: productId
        } = record
        const isUpdateField = record.isUpdate && _.get(record, `updatedField.${PRODUCT_KEY.SPEC_SELECTED_VALUE}`)
        const isReadOnlyField = readOnly && !isUpdateField && !isNew
        const isUpdateAttrField = record.isUpdate && _.get(record, `updatedField.${PRODUCT_KEY.ATTRIBUTES}[0]`)
        const isReadOnlyAttrField = readOnly && !isUpdateAttrField && !isNew
        const layerOneAttribute = _.get(record, `${PRODUCT_KEY.ATTRIBUTES}[0]`, {})
        const isSelectedValueUpdateOnly = !isUpdateAttrField && isUpdateField && readOnly
        const isAttributeUpdateOnly = isUpdateAttrField && !isUpdateField && readOnly
        const readonlyAttributesField = (
          attrsFeatureToggle
            ? (
              <p
                className={clx({
                  [styles.updateField]: record.isUpdate,
                  [styles.isSelectedValueUpdateOnly]: isSelectedValueUpdateOnly
                })}
              >
                {_.get(layerOneAttribute, 'values[0]')}
              </p>
            )
            : null
        )
        const readonlySelectedValueField = (
          <p
            className={clx({
              [styles.updateField]: record.isUpdate,
              [styles.isAttributeUpdateOnly]: isAttributeUpdateOnly
            })}
          >
            {_.get(record, PRODUCT_KEY.SPEC_SELECTED_VALUE)}
          </p>
        )
        if ((isReadOnlyField && isReadOnlyAttrField) || textOnly) {
          return (
            <>
              {readonlyAttributesField}
              {readonlySelectedValueField}
            </>
          )
        }

        const isFakeProductId = checkIsFakeProductId(productId)
        const column = COLUMN.SPEC_SELECTED_VALUE
        const fieldName = getTableField(productId, column, record)
        const attributesColumn = COLUMN.ATTRIBUTES
        const attributesFieldName = getTableField(productId, attributesColumn, record)
        const attributesOptions = _.get(attributesOptionsMapByName, `${specName}.options`, DEFAULT_ATTRIBUTE_OPTIONS)
        const attributesField = (
          attrsFeatureToggle
            ? (
              <Field
                name={`${attributesFieldName}[0].values`}
                component={MenuSelectField}
                data-role='attributes-menu-select'
                className={clx(styles.attrs, { [styles.updateField]: isAttributeUpdateOnly })}
                onChange={e => debounceAttrChange(e, record, `${attributesColumn}[0]`)}
                validate={customAttributesValidation}
                options={attributesOptions}
                enableFilter={false}
                placeholder='請選擇或輸入項目'
                disabled={!editable || isFakeProductId}
                closeOnSelect
                keepErrorPosition
              />
            )
            : null
        )
        const oneLayerListingField = (
          <Field
            name={fieldName}
            component={InputCountField}
            data-role='spec-input'
            className={clx(styles.specSelectedValue, { [styles.isAttributeUpdateOnly]: isAttributeUpdateOnly })}
            onChange={e => debounceAttrChange(e, record, column)}
            countLimit={specCountLimit}
            validate={customInputSpecValidation}
            disabled={!editable || isFakeProductId}
            placeholder='請輸入項目'
            keepErrorPosition
          />
        )
        const twoLayerListingField = (
          <Field
            name={fieldName}
            component={MenuSelectField}
            data-role='spec-menu-select'
            className={clx(styles.specSelectedValue, { [styles.isAttributeUpdateOnly]: isAttributeUpdateOnly })}
            options={specOptions}
            enableFilter={false}
            onChange={e => debounceAttrChange(e, record, column)}
            placeholder='請選擇或輸入項目'
            customInputCountLimit={specCountLimit}
            validateCustomInput={value => customSelectSpecValidation(value, specCountLimit)}
            disabled={!editable || isFakeProductId}
            closeOnSelect
            closeOnCustomInputSubmit
            editableCustomInput
            enableCustomInput
            enableCustomInputValidation
            enableCustomInputCounter
            keepErrorPosition
          />
        )
        return (
          <>
            {/* 屬性項目有變更顯示 input 欄位否則顯示字串 */}
            {isReadOnlyAttrField ? readonlyAttributesField : attributesField}
            {/* 項目顯示名稱未變更顯示字串 */}
            {(isReadOnlyField) && readonlySelectedValueField}
            {/* 單層屬性項目的項目顯示名稱變更顯示 input */}
            {(!isTwoLayerAttr && !isReadOnlyField) && oneLayerListingField}
            {/* 雙層屬性項目的第一層項目顯示名稱變更顯示 select menu */}
            {(isTwoLayerAttr && !isReadOnlyField) && twoLayerListingField}
          </>
        )
      }
    },
    ...(
      isTwoLayerAttr
        ? [{
          title: (
            <span>
              {`${(itemSpecName || '無屬性')} `}
              {
                editable &&
                  <TooltipIcon tooltipTitle={SELECTED_VALUE_TOOLTIP_MSG} />
              }
              {
                attrsFeatureToggle && (
                  <>
                    <br />
                    屬性項目／項目顯示名稱
                    <TooltipIcon tooltipTitle={ATTRIBUTES_TOOLTIP_MSG} />
                    <br />
                    <Tip
                      type='info'
                      className={styles.attributesTip}
                    >
                      {ATTRIBUTES_SYNC_TIP_MSG}
                    </Tip>
                    <br />
                  </>
                )
              }
            </span>
          ),
          width: 200,
          dataIndex: COLUMN.ITEM_SPEC_SELECTED_VALUE,
          key: COLUMN.ITEM_SPEC_SELECTED_VALUE,
          align: 'left',
          render: (text, record) => {
            const {
              isNew,
              [attrMap.productId]: productId
            } = record
            const isUpdateField = (
              record.isUpdate &&
              _.get(record, `updatedField.${PRODUCT_KEY.ITEM_SPEC_SELECTED_VALUE}`)
            )
            const isReadOnlyField = readOnly && !isUpdateField && !isNew
            const isUpdateAttrField = record.isUpdate && _.get(record, `updatedField.${PRODUCT_KEY.ATTRIBUTES}[1]`)
            const isReadOnlyAttrField = readOnly && !isUpdateAttrField && !isNew
            const layerTwoAttribute = _.get(record, `${PRODUCT_KEY.ATTRIBUTES}[1]`, {})
            const isSelectedValueUpdateOnly = !isUpdateAttrField && isUpdateField && readOnly
            const isAttributeUpdateOnly = isUpdateAttrField && !isUpdateField && readOnly
            const readonlyAttributesField = (
              attrsFeatureToggle
                ? (
                  <p
                    className={clx({
                      [styles.updateField]: record.isUpdate,
                      [styles.isSelectedValueUpdateOnly]: isSelectedValueUpdateOnly
                    })}
                  >
                    {_.get(layerTwoAttribute, 'values[0]')}
                  </p>
                )
                : null
            )
            const readonlySelectedValueField = (
              <p
                className={clx({
                  [styles.updateField]: record.isUpdate,
                  [styles.isAttributeUpdateOnly]: isAttributeUpdateOnly
                })}
              >
                {_.get(record, PRODUCT_KEY.ITEM_SPEC_SELECTED_VALUE)}
              </p>
            )
            if ((isReadOnlyField && isReadOnlyAttrField) || textOnly) {
              return (
                <>
                  {readonlyAttributesField}
                  {readonlySelectedValueField}
                </>
              )
            }

            const isFakeProductId = checkIsFakeProductId(productId)
            const column = COLUMN.ITEM_SPEC_SELECTED_VALUE
            const specSelectedValue = _.get(record, PRODUCT_KEY.SPEC_SELECTED_VALUE)
            const attributesColumn = COLUMN.ATTRIBUTES
            const attributesFieldName = getTableField(productId, attributesColumn, record)
            const attributesOptions = _.get(attributesOptionsMapByName, `${itemSpecName}.options`, DEFAULT_ATTRIBUTE_OPTIONS)
            const attributesField = (
              attrsFeatureToggle
                ? (
                  <Field
                    name={`${attributesFieldName}[1].values`}
                    component={MenuSelectField}
                    data-role='attributes-menu-select'
                    className={clx(styles.attrs, { [styles.updateField]: isAttributeUpdateOnly })}
                    onChange={e => debounceAttrChange(e, record, `${attributesColumn}[1]`)}
                    validate={customAttributesValidation}
                    options={attributesOptions}
                    enableFilter={false}
                    placeholder='請選擇或輸入項目'
                    disabled={!editable || isFakeProductId}
                    closeOnSelect
                    keepErrorPosition
                  />
                )
                : null
            )
            const twoLayerListingField = (
              <Field
                name={getTableField(productId, column, record)}
                component={InputCountField}
                data-role='item-spec-input'
                className={styles.itemSpecSelectedValue}
                onChange={e => debounceAttrChange(e, record, column)}
                countLimit={itemSpecCountLimit}
                validate={customInputSpecValidation}
                placeholder='請輸入項目'
                disabled={_.isUndefined(specSelectedValue) || !editable || isFakeProductId}
                keepErrorPosition
              />
            )
            return (
              <>
                {/* 屬性項目有變更顯示 input 欄位否則顯示字串 */}
                {isReadOnlyAttrField ? readonlyAttributesField : attributesField}
                {/* 雙層屬性項目的第二層項目顯示名稱變更 */}
                {(isReadOnlyField) ? readonlySelectedValueField : twoLayerListingField}
              </>
            )
          }
        }]
        : []
    ),
    {
      title: '可售數量',
      width: 90 + (isTwoLayerAttr ? 0 : 105),
      dataIndex: COLUMN.STOCK,
      key: COLUMN.STOCK,
      align: 'right',
      render: (text, record) => {
        // [APACSMDESK-70064][CR][調整賣場屬性項目] 商品庫存調整為可售數量 #1695
        // 在 updateListingModels API 中，更新操作僅需傳入 stock 參數。
        // 因此，在新增商品時直接將輸入的值設為 stock。
        // 在這個階段，沒有 availableCount 參數，所以顯示的是 stock 值。
        const proudctCount = _.get(record, attrMap.availableCount, _.get(record, attrMap.stock, 0))
        return numeralFormat(+proudctCount, { allowZero: true })
      }
    },
    {
      title: '供應商商品料號',
      width: 120 + (isTwoLayerAttr ? 0 : 105),
      key: COLUMN.PART_NO,
      dataIndex: COLUMN.PART_NO,
      render: (text, record) => {
        return _.get(record, attrMap.productPartNo)
      }
    },
    {
      key: 'action',
      width: 110,
      align: 'left',
      render: (text, record) => {
        const actionsVisibleMap = getActionsVisibleMap()
        const specSelectedValue = _.get(record, PRODUCT_KEY.SPEC_SELECTED_VALUE)
        const productId = _.get(record, attrMap.productId)
        const isVisible = _.get(record, attrMap.specVisible)
        const visibleKey = isTwoLayerAttr ? specSelectedValue : `${productId}_${specSelectedValue}`
        const { isVisibleBtn, isDeleteBtn } = _.get(actionsVisibleMap, visibleKey, {})
        const { onProductModalOpen, onChangeProductVisible } = columnsProps
        return (
          <>
            {
              !readOnly && (
                <Button
                  buttonType='outline'
                  size='small'
                  className={clx(
                    listingModelStyles.actionBtn,
                    listingModelStyles.deleteBtn,
                    // 商品狀態已為隱藏狀態時不影響整個賣場能不能顯示商品，所以可以直接被移除
                    {
                      [listingModelStyles.visible]: (
                        (isDeleteBtn || !isVisible) &&
                        !batchRemoveMode
                      )
                    }
                  )}
                  onClick={() => columnsProps.onProductModalOpen([record], PRODUCT_MODAL_TYPE.DELETE)}
                >
                  <Icon
                    name='delete'
                    color='#979ba7'
                    align='middle'
                    size={16}
                  />
                </Button>
              )}
            {
              // isVisibleBtn 判斷屬性可不可以修改顯示狀態
              // isVisible 屬性目前的顯示狀態
              (isVisibleBtn || !isVisible) && (
                <VisibleButton
                  record={record}
                  isTwoLayerAttr={isTwoLayerAttr}
                  onClick={
                    () => isVisible
                      ? onProductModalOpen([record], PRODUCT_MODAL_TYPE.VISIBLE)
                      : onChangeProductVisible(record)
                  }
                  readOnly={!editable}
                  visible={isVisibleBtn}
                />
              )
            }
          </>
        )
      }
    }
  ]
}

class ModelTable extends Component {
  constructor (props) {
    super(props)
    this.sortableTable = createRef()
    this.state = {
      data: [],
      specCountLimit: 0,
      itemSpecCountLimit: 0,
      actionsVisibleMap: {}
    }
    this.tableProps = {
      // 輸入驗證在表格內使用閉包會造成更新的無限迴圈，因此拆到 class 內塞好資料
      customInputSpecValidation: customInputSpecValidation('', []),
      customProductNameValidation: customProductNameValidation(this.props.isExpress24Hr),
      customAttributesValidation: customAttributesValidation(),
      debounceAttrChange: _.debounce(this.onAttrChange, 300),
      getCountLimit: this.getCountLimit,
      getActionsVisibleMap: () => this.state.actionsVisibleMap
    }
    this.locale = {
      emptyText: <NoData />
    }
  }

  componentDidUpdate (prevProps) {
    const isPropsChanged = (
      !_.isEqual(prevProps.tableRows, this.props.tableRows) ||
      // 用於判斷商品屬性可否被選取為批次移除項目
      !_.isEqual(prevProps.form[FORM_SECTION_NAME], this.props.form[FORM_SECTION_NAME])
    )
    if (!isPropsChanged) {
      return
    }
    this.updateTableData()
  }

  getCountLimit = () => {
    return _.pick(this.state, ['specCountLimit', 'itemSpecCountLimit'])
  }

  onAttrChange = (e, record, column) => {
    this.props.onAttrChange(e, record, column)
  }

  updateTableData = () => {
    const {
      listing,
      isTwoLayerAttr,
      tableRows
    } = this.props
    const listingName = _.get(listing, 'name')
    // 每次更新表格資料同時更新屬性可輸入最大值
    const specCountLimit = getSpecCountLimit(listingName, tableRows, COLUMN.SPEC_SELECTED_VALUE)
    // 單層屬性賣場沒有第二層所以不用計算
    const itemSpecCountLimit = isTwoLayerAttr
      ? getSpecCountLimit(listingName, tableRows, COLUMN.ITEM_SPEC_SELECTED_VALUE)
      : 0
    this.tableProps.customInputSpecValidation = customInputSpecValidation(listingName, tableRows)
    this.tableProps.customProductNameValidation = customProductNameValidation(this.props.isExpress24Hr)
    const actionsVisibleMap = getActionsVisibleMapBySelectedValue(this.props)
    this.setState({
      specCountLimit,
      itemSpecCountLimit,
      actionsVisibleMap
    })
  }

  render () {
    const {
      onSortEnd,
      tableRows,
      openNewProductModal,
      readOnly,
      listing,
      sortList,
      isNoLayerAttr,
      isLayerAttrChanged,
      ...restProps
    } = this.props
    const isAllowNewProduct = getIsAllowNewProduct(listing)
    return (
      <FormSection name={FORM_SECTION_NAME}>
        <SortableTable
          ref={this.sortableTable}
          rowClassName={getRowClassName}
          columns={getColumns(this.props, this.tableProps, tableRows)}
          data={tableRows}
          onSortEnd={onSortEnd}
          locale={this.locale}
          listing={listing}
          sortList={sortList}
          readOnly={readOnly}
          {...restProps}
        />
        {
          // 修改頁、有屬性賣場才可以新增商品
          (!readOnly && isAllowNewProduct && !_.isEmpty(sortList) && !isNoLayerAttr) && (
            <NewProductBtnWithPopup
              className={styles.newProductBtn}
              onClick={() => openNewProductModal()}
            />
          )
        }
      </FormSection>
    )
  }
}

export default ModelTable
